import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { championshipsApi, clubsApi, statsApi } from '../lib/api';
import { WeeklyScorersCard } from './leaderboard/PlayerRankingSection';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Trophy,
  Medal,
  Target,
  Shield,
  Users,
  Calendar,
  Play,
  Coins,
  Trash2,
  UserMinus,
  Key,
  Gavel,
  Crown,
} from 'lucide-react';

const PODIUM_STYLES = [
  'order-2 z-10 w-1/3 max-w-[200px] border-t-2 border-x border-[#FFE600] bg-gradient-to-b from-[#FFE600]/20 to-[#0a0d15] rounded-t-xl p-3 md:p-5 pb-8 md:scale-110 shadow-[0_-5px_20px_rgba(255,230,0,0.15)]',
  'order-1 w-1/3 max-w-[180px] border-t-2 border-x border-[#E2E8F0] bg-gradient-to-b from-[#E2E8F0]/15 to-[#0a0d15] rounded-t-xl p-2 md:p-4 pb-6 opacity-95 shadow-[0_-5px_15px_rgba(226,232,240,0.1)]',
  'order-3 w-1/3 max-w-[180px] border-t-2 border-x border-[#CD7F32] bg-gradient-to-b from-[#CD7F32]/15 to-[#0a0d15] rounded-t-xl p-2 md:p-4 pb-4 opacity-95 shadow-[0_-5px_15px_rgba(205,127,50,0.1)]',
];
const PODIUM_ICON_COLOR = ['text-[#FFE600]', 'text-[#E2E8F0]', 'text-[#CD7F32]'];

const PodiumCard = ({ player, rank, stat, statLabel, icon: Icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: rank * 0.12, type: 'spring', stiffness: 120 }}
    whileHover={{ y: -4 }}
    className={`flex flex-col items-center text-center ${PODIUM_STYLES[rank - 1]}`}
    data-testid={`podium-card-${rank}`}
  >
    <Icon size={rank === 1 ? 24 : 18} className={`${PODIUM_ICON_COLOR[rank - 1]} mb-1 drop-shadow-[0_0_6px_currentColor]`} />
    <span className="font-mono text-[10px] text-slate-500">#{rank}</span>
    <p className="font-bold text-white text-xs md:text-sm truncate w-full">{player.player_name}</p>
    <p className="text-slate-400 text-[10px] md:text-xs truncate w-full">{player.club_name}</p>
    <p className="text-3xl md:text-5xl font-mono font-black text-white drop-shadow-md mt-1 tabular-nums">{stat}</p>
    <p className="text-[9px] uppercase tracking-widest text-slate-500">{statLabel}</p>
  </motion.div>
);

const zoneRowClass = (index, total, greenUpTo) => {
  if (index === 0) return 'border-l-4 border-l-[#FFE600] bg-gradient-to-r from-[#FFE600]/10 to-transparent hover:bg-white/10';
  if (index < greenUpTo) return 'border-l-4 border-l-[#00FF87] bg-gradient-to-r from-[#00FF87]/10 to-transparent hover:bg-white/10';
  if (total > 5 && index >= total - 2) return 'border-l-4 border-l-[#FF1744] bg-gradient-to-r from-[#FF1744]/10 to-transparent hover:bg-white/10';
  return 'border-l-4 border-l-transparent hover:bg-white/5';
};

const Championship = () => {
  const { t, i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const { user, isCaptain, isAdmin, isSuperAdmin } = useAuth();
  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(null);
  const [standings, setStandings] = useState([]);
  const [standingsGroups, setStandingsGroups] = useState(null); // {A:[...], B:[...]} when multi-group, else null
  const [topScorerByGroup, setTopScorerByGroup] = useState({}); // { A: {player_name, goals, club_name}, ... }
  const [topAssistByGroup, setTopAssistByGroup] = useState({}); // { A: {player_name, assists, club_name}, ... }
  const [topScorers, setTopScorers] = useState([]);
  const [topAssists, setTopAssists] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [enrolledClubs, setEnrolledClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAccessKeyDialog, setShowAccessKeyDialog] = useState(false);
  const [accessKey, setAccessKey] = useState('');
  const [joiningChampionship, setJoiningChampionship] = useState(false);
  const [weeklyScorers, setWeeklyScorers] = useState([]);

  useEffect(() => {
    statsApi.topScorersWeek(5).then(r => setWeeklyScorers(r.data || [])).catch(() => {});
  }, []);

  // Check if user has a club (can be captain regardless of role)
  const userClub = clubs.find(c => c.captain_id === user?.user_id);
  const canJoinChampionship = !!userClub;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const champsRes = await championshipsApi.list();
        setChampionships(champsRes.data);

        const clubsRes = await clubsApi.list();
        setClubs(clubsRes.data);

        // Select first championship by default
        if (champsRes.data.length > 0) {
          setSelectedChampionship(champsRes.data[0]);
        }
      } catch (error) {
        console.error('Error fetching championships:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedChampionship) return;

    const fetchChampionshipData = async () => {
      try {
        const [standingsRes, scorersRes, assistsRes, clubsRes] = await Promise.all([
          championshipsApi.standings(selectedChampionship.championship_id),
          championshipsApi.topScorers(selectedChampionship.championship_id, 10),
          championshipsApi.topAssists(selectedChampionship.championship_id, 10),
          championshipsApi.getClubs(selectedChampionship.championship_id),
        ]);
        setStandings(Array.isArray(standingsRes.data) ? standingsRes.data : []);
        const isMulti = standingsRes.data && !Array.isArray(standingsRes.data) && standingsRes.data.type === 'multi_group';
        const groupsMap = isMulti ? (standingsRes.data.groups || {}) : null;
        setStandingsGroups(groupsMap);
        setTopScorers(scorersRes.data);
        setTopAssists(assistsRes.data);
        setEnrolledClubs(clubsRes.data);

        // For multi-group, fetch the top scorer + top assistman of each group (just the leader)
        if (groupsMap) {
          const groupLabels = Object.keys(groupsMap);
          const topScByGroup = {};
          const topAsByGroup = {};
          await Promise.all(groupLabels.map(async (g) => {
            try {
              const [scRes, asRes] = await Promise.all([
                fetch(`${window.location.origin}/api/championships/${selectedChampionship.championship_id}/top-scorers?limit=1&group=${g}`),
                fetch(`${window.location.origin}/api/championships/${selectedChampionship.championship_id}/top-assists?limit=1&group=${g}`),
              ]);
              const scData = await scRes.json();
              const asData = await asRes.json();
              if (Array.isArray(scData) && scData.length > 0) topScByGroup[g] = scData[0];
              if (Array.isArray(asData) && asData.length > 0) topAsByGroup[g] = asData[0];
            } catch (_) {}
          }));
          setTopScorerByGroup(topScByGroup);
          setTopAssistByGroup(topAsByGroup);
        } else {
          setTopScorerByGroup({});
          setTopAssistByGroup({});
        }
      } catch (error) {
        console.error('Error fetching championship data:', error);
      }
    };

    fetchChampionshipData();
  }, [selectedChampionship]);

  const handleOpenJoinDialog = () => {
    setAccessKey('');
    setShowAccessKeyDialog(true);
  };

  const handleJoinChampionship = async () => {
    if (!selectedChampionship || !accessKey.trim()) return;

    setJoiningChampionship(true);
    try {
      const response = await championshipsApi.join(selectedChampionship.championship_id, accessKey.trim());
      const entryFee = response.data?.entry_fee_paid || 0;
      
      if (entryFee > 0) {
        toast.success(isItalian 
          ? `Iscrizione completata! Pagati ${entryFee.toLocaleString()} coins` 
          : `Successfully joined! Paid ${entryFee.toLocaleString()} coins`);
      } else {
        toast.success(isItalian ? 'Iscrizione completata!' : 'Successfully joined championship!');
      }

      setShowAccessKeyDialog(false);
      setAccessKey('');

      // Refresh
      const champsRes = await championshipsApi.list();
      setChampionships(champsRes.data);
      const updated = champsRes.data.find(
        (c) => c.championship_id === selectedChampionship.championship_id
      );
      setSelectedChampionship(updated);
      
      // Refresh enrolled clubs
      const clubsRes = await championshipsApi.getClubs(selectedChampionship.championship_id);
      setEnrolledClubs(clubsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore iscrizione' : 'Failed to join'));
    } finally {
      setJoiningChampionship(false);
    }
  };

  const handleRemoveClub = async (clubId, clubName) => {
    if (!selectedChampionship) return;
    
    if (!window.confirm(isItalian 
      ? `Sei sicuro di voler rimuovere "${clubName}" dal campionato?` 
      : `Are you sure you want to remove "${clubName}" from the championship?`)) {
      return;
    }

    try {
      const response = await championshipsApi.removeClub(selectedChampionship.championship_id, clubId);
      const refunded = response.data?.refunded || 0;
      
      if (refunded > 0) {
        toast.success(isItalian 
          ? `Club rimosso! Rimborsati ${refunded.toLocaleString()} coins` 
          : `Club removed! Refunded ${refunded.toLocaleString()} coins`);
      } else {
        toast.success(isItalian ? 'Club rimosso dal campionato' : 'Club removed from championship');
      }

      // Refresh enrolled clubs
      setEnrolledClubs(prev => prev.filter(c => c.club_id !== clubId));
      
      // Refresh championship data
      const champsRes = await championshipsApi.list();
      setChampionships(champsRes.data);
      const updated = champsRes.data.find(
        (c) => c.championship_id === selectedChampionship.championship_id
      );
      setSelectedChampionship(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore rimozione' : 'Failed to remove club'));
    }
  };

  const handleStartChampionship = async () => {
    if (!selectedChampionship) return;

    try {
      await championshipsApi.start(selectedChampionship.championship_id);
      toast.success('Championship started!');

      // Refresh
      const champsRes = await championshipsApi.list();
      setChampionships(champsRes.data);
      const updated = champsRes.data.find(
        (c) => c.championship_id === selectedChampionship.championship_id
      );
      setSelectedChampionship(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore avvio');
    }
  };

  const getClub = (clubId) => clubs.find((c) => c.club_id === clubId);
  const userClubInChampionship =
    selectedChampionship && user?.club_id
      ? selectedChampionship.clubs.includes(user.club_id)
      : false;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <Trophy size={28} className="text-[#FFEA00]" />
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            {t('championship.title')}
          </h1>
        </div>

        {/* Championship Selector */}
        {championships.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {championships.map((champ) => (
              <Button
                key={champ.championship_id}
                variant={selectedChampionship?.championship_id === champ.championship_id ? 'default' : 'outline'}
                className={
                  selectedChampionship?.championship_id === champ.championship_id
                    ? 'btn-primary'
                    : 'btn-secondary'
                }
                onClick={() => setSelectedChampionship(champ)}
              >
                {champ.name}
              </Button>
            ))}
          </div>
        )}
      </motion.div>

      {selectedChampionship ? (
        <>
          {/* Championship Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0a0d15] border border-white/10  p-6"
            data-testid="championship-info"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white">
                  {selectedChampionship.name}
                </h2>
                <p className="text-slate-400">
                  Season {selectedChampionship.season} • {selectedChampionship.clubs.length}/{selectedChampionship.max_teams} teams
                </p>
                {/* Entry Fee Badge */}
                <div className="mt-2">
                  {selectedChampionship.entry_fee > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FFEA00]/20 text-[#FFEA00] text-sm rounded">
                      <Coins size={14} />
                      {isItalian ? 'Quota:' : 'Entry:'} {selectedChampionship.entry_fee.toLocaleString()} coins
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#00E676]/20 text-[#00E676] text-sm rounded">
                      FREE
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <span
                  className={`px-3 py-1 text-xs rounded uppercase tracking-wider ${
                    selectedChampionship.status === 'registration'
                      ? 'bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30'
                      : selectedChampionship.status === 'in_progress'
                      ? 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30'
                      : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                  }`}
                >
                  {selectedChampionship.status === 'registration' 
                    ? (isItalian ? 'Iscrizioni Aperte' : 'Registration Open')
                    : selectedChampionship.status === 'in_progress'
                    ? (isItalian ? 'In Corso' : 'In Progress')
                    : (isItalian ? 'Completato' : 'Completed')}
                </span>

                {selectedChampionship.status === 'registration' && canJoinChampionship && !userClubInChampionship && (
                  <Button
                    className="btn-primary"
                    onClick={handleOpenJoinDialog}
                    data-testid="join-championship-btn"
                  >
                    <Key size={18} className="mr-2" />
                    {isItalian ? 'Iscriviti' : 'Join'}
                    {selectedChampionship.entry_fee > 0 && (
                      <span className="ml-2 text-xs opacity-80">
                        ({selectedChampionship.entry_fee.toLocaleString()} coins)
                      </span>
                    )}
                  </Button>
                )}

                {selectedChampionship.status === 'registration' && canJoinChampionship && userClubInChampionship && (
                  <span className="px-3 py-2 bg-[#00E676]/20 text-[#00E676] rounded text-sm">
                    {isItalian ? '✓ Già iscritto' : '✓ Already joined'}
                  </span>
                )}

                {selectedChampionship.status === 'registration' && isAdmin && (
                  <Button
                    className="bg-[#00E676] hover:bg-[#00E676]/80 text-black"
                    onClick={handleStartChampionship}
                    data-testid="start-championship-btn"
                  >
                    <Play size={18} className="mr-2" />
                    {t('championship.start')}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>

          <Tabs defaultValue="standings" className="space-y-6">
            <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start h-auto p-0 gap-2 md:gap-4 overflow-x-auto flex-nowrap">
              <TabsTrigger
                value="standings"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#00F0FF] data-[state=active]:bg-transparent data-[state=active]:text-[#00F0FF] data-[state=active]:shadow-[0_4px_10px_-4px_rgba(0,240,255,0.5)] px-3 md:px-4 py-3 uppercase tracking-wider text-xs md:text-sm font-bold whitespace-nowrap transition-all"
                data-testid="tab-standings"
              >
                <Trophy size={14} className="mr-2" />
                {t('championship.standings')}
              </TabsTrigger>
              <TabsTrigger
                value="clubs"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#00F0FF] data-[state=active]:bg-transparent data-[state=active]:text-[#00F0FF] data-[state=active]:shadow-[0_4px_10px_-4px_rgba(0,240,255,0.5)] px-3 md:px-4 py-3 uppercase tracking-wider text-xs md:text-sm font-bold whitespace-nowrap transition-all"
                data-testid="tab-clubs"
              >
                <Shield size={14} className="mr-2" />
                {isItalian ? 'Club Iscritti' : 'Enrolled Clubs'} ({enrolledClubs.length})
              </TabsTrigger>
              <TabsTrigger
                value="scorers"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FFE600] data-[state=active]:bg-transparent data-[state=active]:text-[#FFE600] data-[state=active]:shadow-[0_4px_10px_-4px_rgba(255,230,0,0.5)] px-3 md:px-4 py-3 uppercase tracking-wider text-xs md:text-sm font-bold whitespace-nowrap transition-all"
                data-testid="tab-scorers"
              >
                <Medal size={14} className="mr-2" />
                {t('championship.topScorers')}
              </TabsTrigger>
              <TabsTrigger
                value="assists"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#00F0FF] data-[state=active]:bg-transparent data-[state=active]:text-[#00F0FF] data-[state=active]:shadow-[0_4px_10px_-4px_rgba(0,240,255,0.5)] px-3 md:px-4 py-3 uppercase tracking-wider text-xs md:text-sm font-bold whitespace-nowrap transition-all"
                data-testid="tab-assists"
              >
                <Target size={14} className="mr-2" />
                {t('championship.topAssists')}
              </TabsTrigger>
            </TabsList>

            {/* Standings */}
            <TabsContent value="standings">
              {standingsGroups ? (
                <div className="space-y-4">
                  {Object.entries(standingsGroups)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([groupName, groupStandings]) => (
                      <motion.div
                        key={groupName}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-[#0a0d15] border border-white/10  overflow-hidden"
                      >
                        <div className="px-4 py-2 border-b border-white/10 bg-[#050508] flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2">
                            <Shield size={14} className="text-[#00F0FF]" />
                            <span className="text-xs uppercase tracking-wider text-[#00F0FF] font-bold">
                              {isItalian ? `Girone ${groupName}` : `Group ${groupName}`}
                            </span>
                          </div>
                          {(topScorerByGroup[groupName] || topAssistByGroup[groupName]) && (
                            <div className="flex items-center gap-3 text-xs flex-wrap">
                              {topScorerByGroup[groupName] && (
                                <div className="flex items-center gap-1.5">
                                  <Medal size={12} className="text-[#FFD700]" />
                                  <span className="text-slate-500 uppercase tracking-wider text-[10px]">
                                    {isItalian ? 'Capocannoniere' : 'Top Scorer'}:
                                  </span>
                                  <span className="text-white font-medium" data-testid={`top-scorer-${groupName}`}>
                                    {topScorerByGroup[groupName].player_name}
                                  </span>
                                  <span className="font-mono font-bold text-[#FFEA00] tabular-nums">
                                    {topScorerByGroup[groupName].goals} {isItalian ? 'gol' : 'goals'}
                                  </span>
                                </div>
                              )}
                              {topAssistByGroup[groupName] && (
                                <div className="flex items-center gap-1.5 pl-3 border-l border-white/10">
                                  <Target size={12} className="text-[#00F0FF]" />
                                  <span className="text-slate-500 uppercase tracking-wider text-[10px]">
                                    {isItalian ? 'Assistman' : 'Top Assists'}:
                                  </span>
                                  <span className="text-white font-medium" data-testid={`top-assist-${groupName}`}>
                                    {topAssistByGroup[groupName].player_name}
                                  </span>
                                  <span className="font-mono font-bold text-[#00F0FF] tabular-nums">
                                    {topAssistByGroup[groupName].assists} {isItalian ? 'assist' : 'assists'}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="overflow-x-auto w-full">
                        <Table className="min-w-[620px]">
                          <TableHeader>
                            <TableRow className="border-white/10 hover:bg-transparent">
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs w-12">#</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{t('championship.team')}</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.played')}</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.won')}</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center hidden md:table-cell">{t('championship.drawn')}</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center hidden md:table-cell">{t('championship.lost')}</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.gf')}</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.ga')}</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center hidden md:table-cell">{t('championship.gd')}</TableHead>
                              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.points')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(groupStandings || []).map((team, index) => {
                              const club = getClub(team.club_id);
                              return (
                                <TableRow
                                  key={team.club_id}
                                  className={`border-white/10 transition-colors ${zoneRowClass(index, groupStandings?.length || 0, 2)}`}
                                  data-testid={`standings-row-${team.club_id}`}
                                >
                                  <TableCell className="font-mono font-bold text-white">{index + 1}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-3">
                                      <div
                                        className="w-8 h-8 rounded flex items-center justify-center overflow-hidden"
                                        style={{ backgroundColor: club?.colors?.primary || '#2979FF' }}
                                      >
                                        {club?.logo ? (
                                          <img src={club.logo} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                          <Shield size={16} className="text-white/80" />
                                        )}
                                      </div>
                                      <span className="font-medium text-white">{team.club_name}</span>
                                      {team.premium && <Crown size={14} className="text-[#FFE600] drop-shadow-[0_0_5px_#FFE600]" title="Club Premium" />}
                                      {team.forfeit_wins > 0 && (
                                        <span
                                          title={`${team.forfeit_wins} ${team.forfeit_wins === 1 ? 'vittoria' : 'vittorie'} a tavolino`}
                                          data-testid={`forfeit-badge-${team.club_id}`}
                                          className="inline-flex items-center gap-0.5 text-[10px] text-[#FF1744] bg-[#FF1744]/20 border border-[#FF1744]/50 rounded px-1 py-0.5 font-mono"
                                        >
                                          <Gavel size={10} />{team.forfeit_wins}
                                        </span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center text-slate-300">{team.played}</TableCell>
                                  <TableCell className="text-center text-slate-300">{team.won}</TableCell>
                                  <TableCell className="text-center text-slate-300 hidden md:table-cell">{team.drawn}</TableCell>
                                  <TableCell className="text-center text-slate-300 hidden md:table-cell">{team.lost}</TableCell>
                                  <TableCell className="text-center text-slate-300">{team.goals_for}</TableCell>
                                  <TableCell className="text-center text-slate-300">{team.goals_against}</TableCell>
                                  <TableCell
                                    className={`text-center font-mono hidden md:table-cell ${
                                      team.goal_difference > 0
                                        ? 'text-[#00E676]'
                                        : team.goal_difference < 0
                                        ? 'text-[#FF1744]'
                                        : 'text-slate-300'
                                    }`}
                                  >
                                    {team.goal_difference > 0 ? '+' : ''}
                                    {team.goal_difference}
                                  </TableCell>
                                  <TableCell className="text-center font-mono font-bold text-white">{team.points}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        </div>
                      </motion.div>
                    ))}
                </div>
              ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-[#0a0d15] border border-white/10  overflow-hidden"
              >
                <div className="overflow-x-auto w-full">
                <Table className="min-w-[620px]">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs w-12">#</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{t('championship.team')}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.played')}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.won')}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center hidden md:table-cell">{t('championship.drawn')}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center hidden md:table-cell">{t('championship.lost')}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.gf')}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.ga')}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center hidden md:table-cell">{t('championship.gd')}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{t('championship.points')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {standings.map((team, index) => {
                      const club = getClub(team.club_id);
                      return (
                        <TableRow
                          key={team.club_id}
                          className={`border-white/10 transition-colors ${zoneRowClass(index, standings.length, 4)}`}
                          data-testid={`standings-row-${team.club_id}`}
                        >
                          <TableCell className="font-mono font-bold text-white">{index + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div
                                className="w-8 h-8 rounded flex items-center justify-center overflow-hidden"
                                style={{ backgroundColor: club?.colors?.primary || '#2979FF' }}
                              >
                                {club?.logo ? (
                                  <img src={club.logo} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <Shield size={16} className="text-white/80" />
                                )}
                              </div>
                              <span className="font-medium text-white">{team.club_name}</span>
                              {team.premium && <Crown size={14} className="text-[#FFE600] drop-shadow-[0_0_5px_#FFE600]" title="Club Premium" />}
                              {team.forfeit_wins > 0 && (
                                <span
                                  title={`${team.forfeit_wins} ${team.forfeit_wins === 1 ? 'vittoria' : 'vittorie'} a tavolino`}
                                  data-testid={`forfeit-badge-${team.club_id}`}
                                  className="inline-flex items-center gap-0.5 text-[10px] text-[#FF1744] bg-[#FF1744]/20 border border-[#FF1744]/50 rounded px-1 py-0.5 font-mono"
                                >
                                  <Gavel size={10} />{team.forfeit_wins}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-slate-300">{team.played}</TableCell>
                          <TableCell className="text-center text-slate-300">{team.won}</TableCell>
                          <TableCell className="text-center text-slate-300 hidden md:table-cell">{team.drawn}</TableCell>
                          <TableCell className="text-center text-slate-300 hidden md:table-cell">{team.lost}</TableCell>
                          <TableCell className="text-center text-slate-300">{team.goals_for}</TableCell>
                          <TableCell className="text-center text-slate-300">{team.goals_against}</TableCell>
                          <TableCell
                            className={`text-center font-mono hidden md:table-cell ${
                              team.goal_difference > 0
                                ? 'text-[#00E676]'
                                : team.goal_difference < 0
                                ? 'text-[#FF1744]'
                                : 'text-slate-300'
                            }`}
                          >
                            {team.goal_difference > 0 ? '+' : ''}
                            {team.goal_difference}
                          </TableCell>
                          <TableCell className="text-center font-mono font-bold text-white">{team.points}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
                {standings.length === 0 && (
                  <div className="p-8 text-center">
                    <Trophy size={32} className="mx-auto text-slate-600 mb-2" />
                    <p className="text-slate-500">{t('common.noData')}</p>
                  </div>
                )}
              </motion.div>
              )}
            </TabsContent>

            {/* Enrolled Clubs */}
            <TabsContent value="clubs">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-[#0a0d15] border border-white/10  overflow-hidden"
              >
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs w-12">#</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Club' : 'Club'}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Capitano' : 'Captain'}</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{isItalian ? 'Giocatori' : 'Players'}</TableHead>
                      {isSuperAdmin && (
                        <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">{isItalian ? 'Azioni' : 'Actions'}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrolledClubs.map((club, index) => (
                      <TableRow
                        key={club.club_id}
                        className="border-white/10 hover:bg-white/5"
                      >
                        <TableCell className="font-mono text-slate-400">{index + 1}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {club.logo ? (
                              <img src={club.logo} alt={club.name} className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-[#00F0FF]/20 flex items-center justify-center">
                                <Shield size={16} className="text-[#00F0FF]" />
                              </div>
                            )}
                            <span className="font-medium text-white">{club.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-400">{club.captain_name || '-'}</TableCell>
                        <TableCell className="text-center font-mono text-slate-300">{club.players?.length || 0}</TableCell>
                        {isSuperAdmin && (
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/10"
                              onClick={() => handleRemoveClub(club.club_id, club.name)}
                              title={isItalian ? 'Rimuovi dal campionato' : 'Remove from championship'}
                              data-testid={`remove-club-${club.club_id}`}
                            >
                              <UserMinus size={16} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {enrolledClubs.length === 0 && (
                  <div className="p-8 text-center">
                    <Shield size={32} className="mx-auto text-slate-600 mb-2" />
                    <p className="text-slate-500">{isItalian ? 'Nessun club iscritto' : 'No clubs enrolled'}</p>
                  </div>
                )}
              </motion.div>
            </TabsContent>

            {/* Top Scorers */}
            <TabsContent value="scorers">
              <WeeklyScorersCard weeklyScorers={weeklyScorers} isItalian={isItalian} />
              {topScorers.length > 0 && (
                <div className="flex flex-row justify-center items-end gap-2 md:gap-6 mb-8 mt-6 px-2">
                  {topScorers.slice(0, 3).map((p, i) => (
                    <PodiumCard key={p.player_id} player={p} rank={i + 1} stat={p.goals} statLabel={isItalian ? 'Gol' : 'Goals'} icon={Medal} />
                  ))}
                </div>
              )}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-[#0a0d15] border border-white/10 overflow-hidden"
              >
                <div className="overflow-x-auto w-full">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs w-12">#</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Player</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Club</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">Goals</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">Assists</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topScorers.slice(3).map((player, index) => (
                      <TableRow
                        key={player.player_id}
                        className="border-white/10 hover:bg-white/10 transition-colors"
                      >
                        <TableCell className="font-mono font-bold text-white">{index + 4}</TableCell>
                        <TableCell className="font-medium text-white">{player.player_name}</TableCell>
                        <TableCell className="text-slate-400">{player.club_name}</TableCell>
                        <TableCell className="text-center font-mono font-bold text-[#00E676]">{player.goals}</TableCell>
                        <TableCell className="text-center font-mono text-slate-400">{player.assists}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                {topScorers.length === 0 && (
                  <div className="p-8 text-center">
                    <Medal size={32} className="mx-auto text-slate-600 mb-2" />
                    <p className="text-slate-500">{t('common.noData')}</p>
                  </div>
                )}
              </motion.div>
            </TabsContent>

            {/* Top Assists */}
            <TabsContent value="assists">
              {topAssists.length > 0 && (
                <div className="flex flex-row justify-center items-end gap-2 md:gap-6 mb-8 mt-6 px-2">
                  {topAssists.slice(0, 3).map((p, i) => (
                    <PodiumCard key={p.player_id} player={p} rank={i + 1} stat={p.assists} statLabel="Assist" icon={Target} />
                  ))}
                </div>
              )}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-[#0a0d15] border border-white/10 overflow-hidden"
              >
                <div className="overflow-x-auto w-full">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs w-12">#</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Player</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Club</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">Assists</TableHead>
                      <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">Goals</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topAssists.slice(3).map((player, index) => (
                      <TableRow
                        key={player.player_id}
                        className="border-white/10 hover:bg-white/10 transition-colors"
                      >
                        <TableCell className="font-mono font-bold text-white">{index + 4}</TableCell>
                        <TableCell className="font-medium text-white">{player.player_name}</TableCell>
                        <TableCell className="text-slate-400">{player.club_name}</TableCell>
                        <TableCell className="text-center font-mono font-bold text-[#00F0FF]">{player.assists}</TableCell>
                        <TableCell className="text-center font-mono text-slate-400">{player.goals}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                {topAssists.length === 0 && (
                  <div className="p-8 text-center">
                    <Target size={32} className="mx-auto text-slate-600 mb-2" />
                    <p className="text-slate-500">{t('common.noData')}</p>
                  </div>
                )}
              </motion.div>
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <div className="text-center py-12 bg-[#0a0d15] border border-white/10 ">
          <Trophy size={48} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-500">No championships available</p>
        </div>
      )}

      {/* Access Key Dialog */}
      <Dialog open={showAccessKeyDialog} onOpenChange={setShowAccessKeyDialog}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2">
              <Key size={20} className="text-[#00F0FF]" />
              {isItalian ? 'Chiave di Accesso' : 'Access Key'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[#050508] border border-white/10  p-4">
              <p className="text-sm text-slate-400 mb-2">
                {isItalian 
                  ? 'Inserisci la chiave di accesso fornita dal Super Admin per iscrivere il tuo club al campionato.'
                  : 'Enter the access key provided by the Super Admin to register your club in the championship.'}
              </p>
              {selectedChampionship && (
                <p className="text-white font-medium">{selectedChampionship.name}</p>
              )}
              {selectedChampionship?.entry_fee > 0 && (
                <p className="text-[#FFEA00] text-sm mt-1">
                  {isItalian ? 'Costo iscrizione:' : 'Entry fee:'} {selectedChampionship.entry_fee.toLocaleString()} coins
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                {isItalian ? 'Chiave di Accesso' : 'Access Key'}
              </Label>
              <Input
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value.toUpperCase())}
                className="bg-[#050508] border-white/10 text-white  font-mono tracking-widest"
                placeholder="KEY-XXXXXXXXXXXX"
                data-testid="access-key-input"
              />
            </div>

            <Button
              className="w-full btn-primary"
              onClick={handleJoinChampionship}
              disabled={joiningChampionship || !accessKey.trim()}
              data-testid="confirm-join-btn"
            >
              {joiningChampionship ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Trophy size={18} className="mr-2" />
              )}
              {isItalian ? 'Conferma Iscrizione' : 'Confirm Registration'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Championship;
